import type { DropdownOption } from '../types';
import { transformsService } from '../../../api/services/transforms';
import { workflowsService } from '../../../api/services/workflows';
import { apiClient } from '../../../api/client';
import { createContextLogger } from '../../../utils/logger';

const logger = createContextLogger('dropdownCache');

interface CacheEntry {
  options: DropdownOption[];
  timestamp: number;
}

interface CacheKey {
  pipelinePath: string;
  transformType: string;
  componentId: string;
  transformName: string;
  isWorkflow?: boolean; // Add workflow context
}

/**
 * Centralized cache manager for dropdown options that are dynamically loaded from the backend.
 * Cache is scoped per pipeline + transform + component to ensure proper isolation.
 */
class DropdownCacheManager {
  private cache = new Map<string, CacheEntry>();
  private loadingPromises = new Map<string, Promise<DropdownOption[]>>();
  
  // Cache TTL: 5 minutes
  private readonly CACHE_TTL = 5 * 60 * 1000;

  /**
   * Creates a unique cache key for the given parameters
   */
  private createCacheKey(key: CacheKey): string {
    const contextPrefix = key.isWorkflow ? 'workflow' : 'pipeline';
    return `${contextPrefix}:${key.pipelinePath}:${key.transformType}:${key.transformName}:${key.componentId}`;
  }

  /**
   * Checks if a cache entry is still valid
   */
  private isValidCacheEntry(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp < this.CACHE_TTL;
  }

  /**
   * Gets dropdown options from cache or fetches from backend
   */
  async getOptions(key: CacheKey): Promise<DropdownOption[]> {
    const cacheKey = this.createCacheKey(key);
    
    logger.debug('Dropdown cache: checking cache for key:', { 
      key, 
      cacheKey,
      cacheSize: this.cache.size,
      hasEntry: this.cache.has(cacheKey)
    });
    
    // Check if we have a valid cached entry
    const cachedEntry = this.cache.get(cacheKey);
    if (cachedEntry && this.isValidCacheEntry(cachedEntry)) {
      logger.debug('✅ Dropdown cache hit:', { key, optionsCount: cachedEntry.options.length });
      return cachedEntry.options;
    } else if (cachedEntry) {
      logger.debug('⏰ Dropdown cache entry expired:', { key, entryAge: Date.now() - cachedEntry.timestamp });
    } else {
      logger.debug('❌ Dropdown cache miss - no entry found:', { key, cacheKey });
    }

    // Check if we're already loading this entry
    const existingPromise = this.loadingPromises.get(cacheKey);
    if (existingPromise) {
      logger.debug('Dropdown cache: using existing promise:', { key });
      return existingPromise;
    }

    // Fetch from backend
    logger.debug('Dropdown cache miss: fetching from backend:', { key });
    const promise = this.fetchFromBackend(key.transformType, key.componentId, key.pipelinePath, key.transformName, key.isWorkflow);
    
    // Store the promise to prevent duplicate requests
    this.loadingPromises.set(cacheKey, promise);
    
    try {
      const options = await promise;
      
      // Debug: Log the API response summary
      logger.debug('Dropdown cache: API response received:', {
        key,
        isArray: Array.isArray(options),
        optionsCount: options?.length || 0
      });
      
      // Cache the result
      this.cache.set(cacheKey, {
        options,
        timestamp: Date.now()
      });
      
      logger.debug('Dropdown cache: stored new entry:', { 
        key, 
        optionsCount: options.length 
      });
      
      return options;
    } catch (error) {
      logger.error('Dropdown cache: failed to fetch options:', { key, error });
      return [];
    } finally {
      // Remove the loading promise
      this.loadingPromises.delete(cacheKey);
    }
  }

  /**
   * Fetches options from the backend
   */
  private async fetchFromBackend(transformType: string, componentId: string, pipelinePath: string, transformName: string, isWorkflow?: boolean): Promise<DropdownOption[]> {
    if (isWorkflow) {
      // For workflows, use the workflow service
      return workflowsService.getDropdownOptions(transformType, componentId);
    } else {
      // For pipelines, use the transform service
      return transformsService.getDropdownOptions(transformType, componentId, pipelinePath, transformName);
    }
  }

  /**
   * Clears cache entries for a specific pipeline or workflow
   */
  clearPipelineCache(pipelinePath: string): void {
    // Clear our dropdown cache for both pipeline and workflow entries
    const keysToDelete: string[] = [];
    const allCacheKeys: string[] = [];
    
    for (const [cacheKey] of this.cache) {
      allCacheKeys.push(cacheKey);
      // Check for both pipeline:path: and workflow:path: patterns
      if (cacheKey.includes(`:${pipelinePath}:`)) {
        keysToDelete.push(cacheKey);
      }
    }
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.loadingPromises.delete(key);
    });
    
    // Also clear API client cache for all workflow/pipeline endpoints
    const encodedPipelinePath = encodeURIComponent(pipelinePath);
    
    // Clear pipeline endpoint cache patterns
    const pipelineDropdownPattern = `/pipeline/dropdown-options/.*path=${encodedPipelinePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}.*transformName=`;
    const pipelineValuesPattern = `/pipeline/transform-values/.*path=${encodedPipelinePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`;
    const pipelineUIPattern = `/pipeline/transform-ui/`;
    const pipelineDefaultsPattern = `/pipeline/transform-defaults/`;
    
    apiClient.clearCachePattern(pipelineDropdownPattern);
    apiClient.clearCachePattern(pipelineValuesPattern);
    apiClient.clearCachePattern(pipelineUIPattern);
    
    // Clear workflow endpoint cache patterns (different URL structure - no path parameter)
    const workflowDropdownPattern = `/workflow/dropdown-options/`;
    const workflowValuesPattern = `/workflow/action-values/.*path=${encodedPipelinePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`;
    const workflowUIPattern = `/workflow/action-ui/`;
    const workflowDefaultsPattern = `/workflow/action-defaults/`;
    
    apiClient.clearCachePattern(workflowDropdownPattern);
    apiClient.clearCachePattern(workflowValuesPattern);
    apiClient.clearCachePattern(workflowUIPattern);
    apiClient.clearCachePattern(workflowDefaultsPattern);
    
    logger.debug('🧹 Dropdown cache: clearPipelineCache called:', { 
      pipelinePath, 
      encodedPipelinePath,
      pipelinePatterns: [pipelineDropdownPattern, pipelineValuesPattern, pipelineUIPattern, pipelineDefaultsPattern],
      workflowPatterns: [workflowDropdownPattern, workflowValuesPattern, workflowUIPattern, workflowDefaultsPattern],
      totalCacheEntries: allCacheKeys.length,
      allCacheKeys,
      keysToDelete,
      entriesCleared: keysToDelete.length 
    });
  }

  /**
   * Clears cache entries for a specific transform within a pipeline
   */
  clearTransformCache(pipelinePath: string, transformType: string, transformName?: string): void {
    // Clear our dropdown cache
    const keysToDelete: string[] = [];
    const prefix = transformName 
      ? `${pipelinePath}:${transformType}:${transformName}:`
      : `${pipelinePath}:${transformType}:`;
    
    for (const [cacheKey] of this.cache) {
      if (cacheKey.startsWith(prefix)) {
        keysToDelete.push(cacheKey);
      }
    }
    
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.loadingPromises.delete(key);
    });
    
    // Also clear API client cache for this specific transform type and pipeline
    const encodedPipelinePath = encodeURIComponent(pipelinePath);
    const escapedTransformType = transformType.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    let apiCachePattern = `/dropdown-options/${escapedTransformType}/.*path=${encodedPipelinePath.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`;
    
    if (transformName) {
      const escapedTransformName = encodeURIComponent(transformName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      apiCachePattern += `.*transformName=${escapedTransformName}`;
    }
    
    apiClient.clearCachePattern(apiCachePattern);
    
    if (keysToDelete.length > 0) {
      logger.debug('🧹 Dropdown cache: cleared transform cache:', { 
        pipelinePath, 
        transformType,
        transformName,
        encodedPipelinePath,
        apiCachePattern,
        entriesCleared: keysToDelete.length 
      });
    }
  }

  /**
   * Clears all cache entries
   */
  clearAllCache(): void {
    const entriesCleared = this.cache.size;
    this.cache.clear();
    this.loadingPromises.clear();
    
    // Also clear all dropdown-options entries from API client cache
    apiClient.clearCachePattern('/dropdown-options/');
    
    if (entriesCleared > 0) {
      logger.debug('🧹 Dropdown cache: cleared all cache entries (including API cache):', { entriesCleared });
    }
  }

  /**
   * Gets cache statistics for debugging
   */
  getCacheStats() {
    return {
      totalEntries: this.cache.size,
      loadingPromises: this.loadingPromises.size,
      entries: Array.from(this.cache.keys())
    };
  }
}

// Export singleton instance
export const dropdownCache = new DropdownCacheManager();